import maplibregl from 'maplibre-gl'
import { PMTiles } from 'pmtiles'
import { evictMapCacheToCap, getCachedAsset, putCachedAsset } from './mapTileCache'
import { ASSET_UPSTREAM, type TileUpstream } from './mapConfig'

/**
 * `mapcache://` — the request layer in front of every basemap asset (spec §4.3).
 *
 * getMapStyle() points tiles (`mapcache://tile/{z}/{x}/{y}`), glyphs (`mapcache://glyph/{fontstack}/{range}`)
 * and the sprite (`mapcache://sprite/light` → MapLibre appends `.json`/`.png`/`@2x.json`/`@2x.png`)
 * here. Each request is served from the offline map cache (lib/mapTileCache.ts) by its LOGICAL key
 * (tile position / asset name — never the source URL), and only on a miss from whichever upstream
 * is configured. Switching hosted ↔ self-hosted PMTiles is therefore config-only.
 *
 * Offline / failure rules: a missing tile resolves to an EMPTY tile (blank area, no error storm);
 * a missing glyph or sprite throws (MapLibre reports and moves on). Only real answers are stored:
 * a 204/404 tile is a real "nothing here" and is cached empty; a network error or 5xx is not.
 */

const PREFIX = 'mapcache://'
const EMPTY = () => new ArrayBuffer(0)

/** 'mapcache://tile/14/1/2' → 'tile/14/1/2'; glyph fontstacks are URL-decoded. */
export function cacheKeyFor(url: string): string {
  const rest = url.startsWith(PREFIX) ? url.slice(PREFIX.length) : url
  const path = rest.split(/[?#]/)[0]
  try {
    return decodeURIComponent(path)
  } catch {
    return path
  }
}

function parseTile(key: string): { z: number; x: number; y: number } | null {
  const m = /^tile\/(\d+)\/(\d+)\/(\d+)$/.exec(key)
  return m ? { z: Number(m[1]), x: Number(m[2]), y: Number(m[3]) } : null
}

/** The upstream URL for a key, or null where there is none (pmtiles tiles go via getZxy). */
export function upstreamUrlFor(key: string, up: TileUpstream): string | null {
  const tile = parseTile(key)
  if (tile) {
    if (up.kind !== 'hosted') return null
    return up.template
      .replace('{z}', String(tile.z))
      .replace('{x}', String(tile.x))
      .replace('{y}', String(tile.y))
  }
  const glyph = /^glyph\/(.+)\/([^/]+)$/.exec(key)
  if (glyph) return `${ASSET_UPSTREAM}/fonts/${encodeURIComponent(glyph[1])}/${glyph[2]}.pbf`
  const sprite = /^sprite\/([^/]+)$/.exec(key)
  if (sprite) return `${ASSET_UPSTREAM}/sprites/v4/${sprite[1]}`
  return null
}

// One shared PMTiles instance per archive URL (it holds the header/directory cache).
const pmtilesByUrl = new Map<string, PMTiles>()
function pmtilesFor(url: string): PMTiles {
  let p = pmtilesByUrl.get(url)
  if (!p) {
    p = new PMTiles(url)
    pmtilesByUrl.set(url, p)
  }
  return p
}

/**
 * [A3 final review] The map style is shared by EVERY map in the app (organizer analytics maps too), so every
 * viewed tile is stored — the 50 MB cap can't wait for a canvasser's walk save. Enforce it every
 * EVICT_EVERY_STORES stores and once at registration. A sweep already running absorbs further triggers.
 */
export const EVICT_EVERY_STORES = 200
let storesSinceEvict = 0
let evicting: Promise<void> | null = null

function scheduleEviction(): void {
  if (evicting) return
  evicting = evictMapCacheToCap()
    .then(
      () => undefined,
      () => undefined // non-fatal: the next trigger tries again
    )
    .finally(() => {
      evicting = null
    })
}

async function store(key: string, data: ArrayBuffer): Promise<void> {
  try {
    await putCachedAsset(key, data)
  } catch {
    // Non-fatal: the map still gets its data, it just isn't kept for offline.
    return
  }
  if (++storesSinceEvict >= EVICT_EVERY_STORES) {
    storesSinceEvict = 0
    scheduleEviction()
  }
}

/** Test helper: reset the store counter and wait out any running sweep. */
export async function __resetEvictionForTests(): Promise<void> {
  await evicting
  storesSinceEvict = 0
}

function rethrowIfAborted(signal: AbortSignal | undefined, err: unknown): void {
  if (signal?.aborted) throw err
}

async function loadTile(
  key: string,
  tile: { z: number; x: number; y: number },
  up: TileUpstream,
  signal?: AbortSignal
): Promise<ArrayBuffer> {
  if (up.kind === 'none') return EMPTY()

  if (up.kind === 'pmtiles') {
    let res: Awaited<ReturnType<PMTiles['getZxy']>>
    try {
      res = await pmtilesFor(up.url).getZxy(tile.z, tile.x, tile.y, signal)
    } catch (err) {
      rethrowIfAborted(signal, err)
      return EMPTY() // unreachable archive: offline in practice — blank, not stored
    }
    const data = res?.data ?? EMPTY() // undefined = tile absent from the archive (a real answer)
    await store(key, data)
    return data
  }

  let resp: Response
  try {
    resp = await fetch(upstreamUrlFor(key, up)!, { signal })
  } catch (err) {
    rethrowIfAborted(signal, err)
    return EMPTY() // network error: offline in practice even if navigator.onLine says otherwise
  }
  if (resp.status === 204 || resp.status === 404) {
    const data = EMPTY()
    await store(key, data)
    return data
  }
  if (!resp.ok) return EMPTY() // 5xx / auth failure: blank this time, never cached
  const data = await resp.arrayBuffer()
  await store(key, data)
  return data
}

/** Serve one basemap asset: cache → (online) upstream → store. See the module comment for rules. */
export async function loadMapAsset(
  key: string,
  up: TileUpstream,
  opts: { signal?: AbortSignal } = {}
): Promise<ArrayBuffer> {
  const cached = await getCachedAsset(key)
  if (cached !== null) return cached // an empty buffer is a valid hit (cached 204/404 tile)

  const tile = parseTile(key)
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false

  if (tile) {
    if (offline) return EMPTY()
    return loadTile(key, tile, up, opts.signal)
  }

  if (offline) throw new Error(`Map asset not saved for offline: ${key}`)
  const url = upstreamUrlFor(key, up)
  if (!url) throw new Error(`Unknown map asset: ${key}`)
  const resp = await fetch(url, { signal: opts.signal })
  if (!resp.ok) throw new Error(`Map asset ${key} failed: HTTP ${resp.status}`)
  const data = await resp.arrayBuffer()
  await store(key, data)
  return data
}

/**
 * Register the `mapcache://` protocol once, in front of the given upstream. [Native B1] Pass a
 * getter to resolve the upstream per request: on native the instance's tile settings arrive after
 * registration (lib/runtimeConfig.ts), so a value captured here would be stale or empty.
 */
export function registerMapCacheProtocol(up: TileUpstream | (() => TileUpstream)): void {
  const resolve = typeof up === 'function' ? up : () => up
  scheduleEviction() // trim whatever earlier sessions left over the cap
  maplibregl.addProtocol('mapcache', async (params, abortController) => {
    const data = await loadMapAsset(cacheKeyFor(params.url), resolve(), { signal: abortController.signal })
    // A custom protocol must hand back what MapLibre asked for: sprite JSON is requested as
    // type 'json' and is used as-is, so parse it here. Images/tiles/glyphs take the ArrayBuffer.
    if (params.type === 'json') return { data: JSON.parse(new TextDecoder().decode(data)) }
    return { data }
  })
}
